import React, { useCallback } from 'react';
import { faArrowUpRightFromSquare } from '@fortawesome/pro-solid-svg-icons/faArrowUpRightFromSquare';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { CheckboxSlider } from 'ui-components/components/checkbox-slider';
import { FlexColumn, Gap } from 'ui-components/components/flex';
import { Text } from 'ui-components/components/text/text';
import { TextType } from 'ui-components/components/text/text-type';
import { NotificationService, NOTIFICATION_TYPE_ERROR } from 'ui-components/services/notification-service';

import { InsuranceCard } from './insurance-card';
import { messages, parcelGuardMessages, xCoverMessages } from './messages';
import commonStyles from '../../settings-page.module.less';
import styles from './walleted-insurance.module.less';
import { WhyInsuranceCard } from './why-insurance-card';
import { WalletBalanceInfo } from '../../../../components/wallet-balance-info';
import { INSURANCE_SETTINGS_TOGGLE } from '../../../../constants/analytics-events';
import { InsuranceProviders, getInsuranceProviderById } from '../../../../enums/insurance-providers';
import { SellerSettings } from '../../../../enums/seller-settings/seller-settings';
import { InsuranceTermsLink } from '../../../../insurance/terms-link';
import { useTrackInsuranceEvent } from '../../../../insurance/hooks/use-track-insurance-event';
import { getAutomationsSettingsPath } from '../../../../routes/constants';
import {
  shouldHideNonWalletInsuranceOverride,
  useCarrierInsuranceSellerSetting,
  useExternalInsuranceSellerSetting,
  useShouldAutoInsure,
  useWalletedInsuranceActivated,
  useWalletedInsuranceProviderId,
} from '../../../../services/insurance-service';
import { saveNewSellerSettings } from '../../../../store/seller/seller.actions';
import { EventSourceStrings } from '../../../../tracking/tracking-constants';
import { updateSellerSettingsBulk } from '../../api/save-settings';
import { SettingsPageTitle } from '../../components/settings-page-title/settings-page-title';

export const WalletedInsuranceLegacy = () => {
  const dispatch = useDispatch();
  const intl = useIntl();

  const insuranceProviderId = useWalletedInsuranceProviderId();
  const isInsuranceActivated = useWalletedInsuranceActivated();
  const { shouldAutoInsure, setShouldAutoInsure } = useShouldAutoInsure(insuranceProviderId);
  const shouldHideNonWalletInsurance = useSelector(shouldHideNonWalletInsuranceOverride);
  const carrierInsuranceCheckedSellerSetting = useCarrierInsuranceSellerSetting();
  const externalInsuranceCheckedSellerSetting = useExternalInsuranceSellerSetting();

  const carrierMessages = insuranceProviderId === InsuranceProviders.XCover.insuranceProviderId ? xCoverMessages : parcelGuardMessages;
  const insuranceProviderName = getInsuranceProviderById(insuranceProviderId)?.name;

  const trackSettingsToggle = useTrackInsuranceEvent(INSURANCE_SETTINGS_TOGGLE, EventSourceStrings.INSURANCE_SETTINGS);

  const carrierInsuranceOnChange = useCallback(async () => {
    const sellerSettings = {
      [SellerSettings.insuranceCarrierEnabled.key]: !carrierInsuranceCheckedSellerSetting,
    };
    try {
      const newSettings = await updateSellerSettingsBulk(sellerSettings);
      dispatch(saveNewSellerSettings(newSettings));
    } catch {
      NotificationService.showNotification({
        notificationType: NOTIFICATION_TYPE_ERROR,
        content: intl.formatMessage(messages.savePreferencesError),
      });
    }
  }, [dispatch, intl, carrierInsuranceCheckedSellerSetting]);

  const externalInsuranceOnChange = useCallback(async () => {
    const sellerSettings = {
      [SellerSettings.insuranceExternalEnabled.key]: !externalInsuranceCheckedSellerSetting,
    };
    try {
      const newSettings = await updateSellerSettingsBulk(sellerSettings);
      dispatch(saveNewSellerSettings(newSettings));
    } catch {
      NotificationService.showNotification({
        notificationType: NOTIFICATION_TYPE_ERROR,
        content: intl.formatMessage(messages.savePreferencesError),
      });
    }
  }, [dispatch, intl, externalInsuranceCheckedSellerSetting]);

  if (!insuranceProviderId) {
    return null;
  }

  return (
    <div className={commonStyles.content}>
      <div className={styles.container}>
        <div className={styles['title-content']}>
          <SettingsPageTitle value={messages.title} />
          <Text
            className={styles.description}
            formattedMessageValues={{
              a: (chunks) => (
                <InsuranceTermsLink insuranceProviderId={insuranceProviderId} source={EventSourceStrings.INSURANCE_SETTINGS}>
                  {chunks} <FontAwesomeIcon className={styles['external-link-icon']} icon={faArrowUpRightFromSquare} />
                </InsuranceTermsLink>
              ),
            }}
            textType={TextType.Description}
            value={carrierMessages.description}
          />
        </div>
        <div className={styles['two-column-layout']}>
          <div className={styles['left-column']}>
            <InsuranceCard insuranceProviderId={insuranceProviderId} isInsuranceActivated={isInsuranceActivated} />
            {isInsuranceActivated && (
              <div className={styles['insurance-settings-card']}>
                <div className={styles['apply-insurance-section']}>
                  <Text
                    className={styles['apply-insurance-title']}
                    element='span'
                    textType={TextType.Label}
                    value={carrierMessages.applyInsuranceSectionTitle}
                  />
                  <span className={styles['apply-insurance-toggle-section']}>
                    <CheckboxSlider
                      checkboxId='apply-insurance-toggle'
                      checked={shouldAutoInsure}
                      className={styles['insurance-slider']}
                      labelText={<Text textType={TextType.Paragraph} value={messages.applyInsuranceToggleDetails} />}
                      onChange={() => {
                        setShouldAutoInsure(!shouldAutoInsure);
                        trackSettingsToggle({
                          insuranceProvider: insuranceProviderName,
                          toggleName: 'autoInsure',
                          toggleValue: !shouldAutoInsure,
                        });
                      }}
                    />
                  </span>
                  <Text
                    className={styles['automation-rules-message']}
                    formattedMessageValues={{
                      a: (chunks) => <Link to={getAutomationsSettingsPath()}>{chunks}</Link>,
                    }}
                    textType={TextType.Description}
                    value={messages.helpLinkAutomationRules}
                  />
                </div>
                {!shouldHideNonWalletInsurance && (
                  <>
                    <div className={styles['section-separator']} />
                    <div className={styles['apply-insurance-section']}>
                      <Text
                        className={styles['apply-insurance-title']}
                        element='span'
                        textType={TextType.Label}
                        value={messages.applyOtherInsuranceSectionTitle}
                      />
                      <span className={styles['apply-insurance-toggle-section']}>
                        <CheckboxSlider
                          checkboxId='carrier-insurance-toggle'
                          checked={carrierInsuranceCheckedSellerSetting}
                          className={styles['insurance-slider']}
                          labelText={<Text textType={TextType.Paragraph} value={messages.applyCarrierInsuranceToggleTitle} />}
                          onChange={() => {
                            carrierInsuranceOnChange();
                            trackSettingsToggle({
                              insuranceProvider: insuranceProviderName,
                              toggleName: 'carrierInsurance',
                              toggleValue: !carrierInsuranceCheckedSellerSetting,
                            });
                          }}
                        />
                      </span>
                      <Text
                        className={styles['apply-insurance-details-description']}
                        textType={TextType.Paragraph}
                        value={messages.applyCarrierInsuranceToggleDetails}
                      />
                      <span className={styles['apply-insurance-toggle-section']}>
                        <CheckboxSlider
                          checkboxId='external-insurance-toggle'
                          checked={externalInsuranceCheckedSellerSetting}
                          className={styles['insurance-slider']}
                          labelText={<Text textType={TextType.Paragraph} value={messages.applyOtherInsuranceToggleTitle} />}
                          onChange={() => {
                            externalInsuranceOnChange();
                            trackSettingsToggle({
                              insuranceProvider: insuranceProviderName,
                              toggleName: 'externalInsurance',
                              toggleValue: !externalInsuranceCheckedSellerSetting,
                            });
                          }}
                        />
                      </span>
                      <Text
                        className={styles['apply-insurance-details-description']}
                        textType={TextType.Paragraph}
                        value={messages.applyOtherInsuranceToggleDetails}
                      />
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
          <FlexColumn className={styles['right-column']} gap={Gap.Space24}>
            <WalletBalanceInfo isFullSize trackingId={EventSourceStrings.INSURANCE_SETTINGS} />
            {!isInsuranceActivated && <WhyInsuranceCard insuranceProviderId={insuranceProviderId} />}
          </FlexColumn>
        </div>
      </div>
    </div>
  );
};
